import type { SponsorPlanItem } from '@app/_proto/Protos/sponsors';
import type { SubscriptionItem } from '@app/_proto/Protos/subscriptions';
import SubscriptionsButton from '@app/components/subscriptions/subscriptions.button';
import { decimalToNumber } from '@app/libs/utils';
import { useHtmlToReact } from '@app/utils/html';
import { formatAmountForDisplay } from '@app/utils/utils';
import { Skeleton, type SkeletonProps } from '@mui/material';
import clsx from 'clsx';
import * as React from 'react';
import { useCallback } from 'react';

//#region : Global constants and types

/** Custom. List of allowed actions.  */
export type SponsorSelectionType = 'subscribe' | 'upgrade' | 'downgrade' | 'reactivate' | 'unsubscribe' | 'none';

export enum SponsorPlanViewType {
    Full = 0,
    Compact = 1,
}
//#endregion : Global constants and types

//#region : Helpers

/** Custom. Get action text based on the provided type. */
const PlaceholderSkeleton = React.memo(function PlacehlderSkeleton({ className, ...rest }: SkeletonProps) {
    return <Skeleton className={clsx('bg-gray-bg-base', className)} {...rest} />;
});

export const SponsorPlanPlaceholder = React.memo(function SponsorPlanPlaceholder() {
    return (
        <div className="flex flex-row items-center justify-between rounded-[6px] bg-gray-100 p-[16px] dark:bg-gray-850 sm:flex-col sm:justify-center sm:space-y-[16px] sm:text-center">
            <div className="flex-1">
                <PlaceholderSkeleton className="h-[20px] w-[109px] sm:mx-auto" variant="rectangular" animation="wave" />
                <PlaceholderSkeleton
                    className="mt-[8px] mb-[10px] h-[14px] w-[79px] sm:mx-auto"
                    variant="rectangular"
                    animation="wave"
                />
                <PlaceholderSkeleton className="h-[20px] w-[129px] sm:mx-auto" variant="rectangular" animation="wave" />
            </div>
            <div>
                <PlaceholderSkeleton
                    className="h-[35px] w-[129px] rounded-[19.5px]"
                    variant="rectangular"
                    animation="wave"
                />
            </div>
        </div>
    );
});
//#endregion : Helpers

//#region : Main component

/** Custom. Main component parameters type */
export type SponsorPlanProps = {
    plan: SponsorPlanItem;
    currentSubscription?: SubscriptionItem | null;
    selectionType: SponsorSelectionType;
    viewType: SponsorPlanViewType;
    onProcessingStateChange: (processing: boolean) => void;
    onSelectPlan?: (plan: SponsorPlanItem) => void;
};

/**
 * Custom.
 *
 * Displays the sponsor plan details and handles its action.
 */
export default function SponsorPlan({
    plan,
    selectionType,
    viewType,
    currentSubscription,
    onSelectPlan,
    onProcessingStateChange,
}: SponsorPlanProps) {
    const onClickSubscribe = useCallback(() => {
        onSelectPlan?.(plan);
    }, [onSelectPlan, plan]);

    const planDescription = useHtmlToReact(plan.description?.value ?? '', {
        removeTags: ['hr', 'img'],
    });

    if (!plan) {
        return null;
    }

    return (
        <div className="h-full w-full rounded-[12px] bg-gray-bg-elevate py-[12px] pl-[18px] pr-[12px] shadow sm:py-[16px]">
            {viewType === SponsorPlanViewType.Full ? (
                <div className="grid h-full grid-rows-[1fr_min-content]">
                    <div>
                        <div className="font-set-sb18 tracking-[-0.01em] text-gray-t1">{plan.name}</div>
                        <div className="font-set-sb15 py-[4px] text-gray-800 dark:text-gray-400">
                            {plan.advanceChapterCount === -1
                                ? 'All Advance Chapters'
                                : plan.advanceChapterCount +
                                  ' Advance Chapter' +
                                  (plan.advanceChapterCount > 1 ? 's' : '')}
                        </div>
                        <div>
                            <div className="font-set-r13 text-gray-desc">{planDescription}</div>
                        </div>
                    </div>
                    <div className="mt-[4px] flex items-center justify-between">
                        <div className="font-set-r13 text-gray-desc">
                            {selectionType === 'unsubscribe' && (
                                <div className="font-set-sb12 pb-[2px] text-blue">Subscribing</div>
                            )}
                            <span className="font-set-sb18 text-blue">
                                {formatAmountForDisplay(decimalToNumber(plan.price))}
                            </span>{' '}
                            / month
                        </div>
                        <SubscriptionsButton
                            mode="confirm"
                            plan={plan}
                            subscription={currentSubscription}
                            onSubscribed={onClickSubscribe}
                            onProcessingStateChange={onProcessingStateChange}
                        />
                    </div>
                </div>
            ) : (
                <>
                    <div className="font-set-sb16 tracking-[-0.015em] text-gray-t1">{plan.name}</div>
                    <div className="grid grid-cols-[1fr_108px] items-end">
                        <div>
                            <div className="font-set-sb13 pt-[4px] text-gray-desc">
                                {plan.advanceChapterCount === -1
                                    ? 'All Advance Chapters'
                                    : plan.advanceChapterCount +
                                      ' Advance Chapter' +
                                      (plan.advanceChapterCount > 1 ? 's' : '')}
                            </div>
                            {selectionType === 'unsubscribe' && (
                                <div className="font-set-sb12 pt-[4px] text-blue">Subscribing</div>
                            )}
                            <div className="font-set-r13 pt-[4px] text-gray-desc">
                                <span className="font-set-sb17 text-blue">
                                    {formatAmountForDisplay(decimalToNumber(plan.price))}
                                </span>{' '}
                                / month
                            </div>
                        </div>
                        <SubscriptionsButton
                            mode="confirm"
                            plan={plan}
                            subscription={currentSubscription}
                            onSubscribed={onClickSubscribe}
                            onProcessingStateChange={onProcessingStateChange}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

//#endregion : Main component
